import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { maskCPF, maskPhone } from "@/lib/utils-app";
import { useAuth } from "@/lib/auth";
import { useStores } from "@/lib/stores";
import { toast } from "sonner";
import { Copy, CheckCircle2, Printer, Store as StoreIcon, AlertTriangle } from "lucide-react";

export default function AdminNewOS() {
  const { user } = useAuth();
  const { stores, activeStore, activeStoreObj } = useStores();
  const isSuper = user?.role === "superadmin";
  const targetStore = isSuper ? activeStore : user?.store_id;
  const targetStoreObj = isSuper ? activeStoreObj : stores.find((s) => s.store_id === user?.store_id);

  const [f, setF] = useState({
    customer_name: "", customer_surname: "", customer_doc: "", customer_email: "",
    customer_phone: "", device_brand: "", device_model: "", device_serial: "", problem_report: "",
  });
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => {
    let v = e.target.value;
    if (k === "customer_doc") v = maskCPF(v);
    if (k === "customer_phone") v = maskPhone(v);
    setF({ ...f, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isSuper && !targetStore) return toast.error("Selecione uma loja no menu lateral antes de cadastrar.");
    setLoading(true);
    try {
      const payload = { ...f, customer_doc: f.customer_doc.replace(/\D/g, "") };
      const qs = isSuper && targetStore ? `?store_id=${targetStore}` : "";
      const { data } = await api.post(`/admin/service-orders${qs}`, payload);
      setCreated(data);
      toast.success(`OS ${data.os_code} gerada!`);
      setF({ customer_name: "", customer_surname: "", customer_doc: "", customer_email: "", customer_phone: "", device_brand: "", device_model: "", device_serial: "", problem_report: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl">
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Novo atendimento</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Gerar Ordem de Serviço</h1>

      {isSuper && !targetStore && (
        <div className="mt-6 border border-amber-300 bg-amber-50 text-amber-900 rounded-xl p-4 flex items-start gap-3" data-testid="no-store-warning">
          <AlertTriangle size={18} className="mt-0.5"/>
          <div>
            <div className="font-semibold">Selecione uma loja</div>
            <div className="text-sm">Como super-admin, escolha uma loja específica no menu lateral (Contexto de loja) para gerar OS. O prefixo do código virá da loja escolhida.</div>
          </div>
        </div>
      )}
      {targetStoreObj && (
        <div className="mt-4 inline-flex items-center gap-2 text-xs bg-neutral-900 text-white px-3 py-1.5 rounded-full">
          <StoreIcon size={12}/> {targetStoreObj.name} · próximo código <span className="font-mono">{targetStoreObj.code}-XXXXX</span>
        </div>
      )}

      {created && (
        <div className="mt-8 bg-neutral-900 text-white rounded-2xl p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="new-os-success">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400">Código gerado — repasse ao cliente</div>
            <div className="font-mono text-4xl font-bold mt-1">{created.os_code}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="bg-transparent border-neutral-700 text-white hover:bg-neutral-800" onClick={() => { navigator.clipboard.writeText(created.os_code); toast.success("Copiado!"); }} data-testid="copy-os">
              <Copy size={14} className="mr-1.5"/> Copiar
            </Button>
            <Button variant="outline" className="bg-transparent border-neutral-700 text-white hover:bg-neutral-800" onClick={() => window.open(`/admin/etiqueta/${created.os_code}`, "_blank", "width=520,height=520")} data-testid="print-label">
              <Printer size={14} className="mr-1.5"/> Imprimir etiqueta
            </Button>
            <Button className="bg-white text-neutral-900 hover:bg-neutral-100" onClick={() => setCreated(null)}>
              <CheckCircle2 size={14} className="mr-1.5"/> Novo cadastro
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 grid gap-6" data-testid="new-os-form">
        <fieldset className="bg-white border border-neutral-200 rounded-xl p-6">
          <legend className="text-xs uppercase tracking-widest font-bold text-neutral-500 px-2">Cliente</legend>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div><Label>Nome</Label><Input value={f.customer_name} onChange={set("customer_name")} className="mt-1.5 h-11" data-testid="new-name" required /></div>
            <div><Label>Sobrenome</Label><Input value={f.customer_surname} onChange={set("customer_surname")} className="mt-1.5 h-11" data-testid="new-surname" required /></div>
            <div><Label>CPF ou CNPJ</Label><Input value={f.customer_doc} onChange={set("customer_doc")} className="mt-1.5 h-11 font-mono" data-testid="new-doc" required /></div>
            <div><Label>Telefone / WhatsApp</Label><Input value={f.customer_phone} onChange={set("customer_phone")} className="mt-1.5 h-11 font-mono" data-testid="new-phone" required /></div>
            <div className="sm:col-span-2"><Label>E-mail (opcional)</Label><Input type="email" value={f.customer_email} onChange={set("customer_email")} className="mt-1.5 h-11" data-testid="new-email" /></div>
          </div>
        </fieldset>

        <fieldset className="bg-white border border-neutral-200 rounded-xl p-6">
          <legend className="text-xs uppercase tracking-widest font-bold text-neutral-500 px-2">Aparelho</legend>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div><Label>Marca</Label><Input value={f.device_brand} onChange={set("device_brand")} className="mt-1.5 h-11" data-testid="new-brand" required /></div>
            <div><Label>Modelo</Label><Input value={f.device_model} onChange={set("device_model")} className="mt-1.5 h-11" data-testid="new-model" required /></div>
            <div className="sm:col-span-2"><Label>Nº de série / IMEI</Label><Input value={f.device_serial} onChange={set("device_serial")} className="mt-1.5 h-11 font-mono" data-testid="new-serial" /></div>
            <div className="sm:col-span-2"><Label>Relato do problema</Label><Textarea value={f.problem_report} onChange={set("problem_report")} className="mt-1.5 min-h-[100px]" data-testid="new-problem" required /></div>
          </div>
        </fieldset>

        <Button type="submit" disabled={loading} className="h-14 rounded-md bg-neutral-900 hover:bg-neutral-800 text-base" data-testid="new-os-submit">
          {loading ? "Gerando…" : "Gerar Código"}
        </Button>
      </form>
    </div>
  );
}
